import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { ChevronLeft } from "lucide-react";

/**
 * PageHeader — title/subtitle + optional back button (hidden on home).
 * Use on every internal page; do NOT use on `/` (home dashboard).
 *
 * Props:
 *  - title (string | node) — required
 *  - subtitle (string | node) — optional
 *  - actions (node) — optional right-side actions
 *  - hideBack (boolean) — force-hide the back button (e.g. landing pages)
 */
export function PageHeader({ title, subtitle, actions, hideBack = false }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const loc = useLocation();
  const isHome = loc.pathname === "/" || loc.pathname === "";
  // Show back when: not on home, not explicitly hidden, browser has history
  const hasHistory = typeof window !== "undefined" && window.history.length > 1;
  const showBack = !isHome && !hideBack && hasHistory;

  return (
    <div className="flex items-start justify-between gap-3 flex-wrap mb-5">
      <div className="min-w-0">
        {showBack && (
          <button
            type="button"
            onClick={() => navigate(-1)}
            data-testid="page-back-btn"
            aria-label={t("common.back")}
            className="inline-flex items-center gap-1.5 mb-2 text-[11px] uppercase tracking-[0.18em] text-foreground/55 hover:text-foreground transition-colors rounded-md px-2 py-1 hover:bg-white/5"
          >
            <ChevronLeft className="h-3.5 w-3.5" />
            {t("common.back")}
          </button>
        )}
        {typeof title === "string"
          ? <h1 className="font-heading text-3xl sm:text-4xl uppercase tracking-[0.08em] font-extrabold leading-tight">{title}</h1>
          : title}
        {subtitle && (typeof subtitle === "string"
          ? <p className="text-sm text-foreground/65 mt-1">{subtitle}</p>
          : <div className="mt-1">{subtitle}</div>)}
      </div>
      {actions && <div className="flex gap-2 flex-wrap shrink-0">{actions}</div>}
    </div>
  );
}

export default PageHeader;
